/**
 * Choropleth classification engine (M9 shared-engine slice).
 *
 * Turns a variable's value set + a chosen method into class BREAK edges and
 * per-class TOKEN-KEYED color stops (`{value, tokenId}`) — never raw hex. Three
 * methods: `quantile` (breaks by rank, outlier-robust), `equal` (equal-interval
 * over the min→max range), and `manual` (caller-supplied breaks). Pure and fully
 * unit-tested; the numeric-break computation reuses `@/lib/map/color.numericBreaks`
 * (the same math the M4 numeric legend uses) so the overlay and marker ramps stay
 * consistent.
 */

import { numericBreaks } from "@/lib/map/color";
import type { MapTokens } from "@/lib/map";
import type { Choropleth, Classification, ClassificationMethod } from "./types";
import { DEFAULT_RAMP_FAMILY, rampLength, sampleRampStops } from "./ramp";

export const DEFAULT_CLASS_COUNT = 5;

/** Validate + normalize manual break edges: finite, ascending, at least two. */
function normalizeManualBreaks(breaks: number[] | undefined): number[] {
  const finite = (breaks ?? []).filter((n) => Number.isFinite(n));
  if (finite.length < 2) {
    throw new Error("manual classification requires at least two ascending break edges");
  }
  for (let i = 1; i < finite.length; i++) {
    if (finite[i]! <= finite[i - 1]!) {
      throw new Error("manual classification breaks must be strictly ascending");
    }
  }
  return finite;
}

/**
 * Compute a choropleth classification.
 *
 * @param values  every boundary's value for the variable (non-finite dropped).
 * @param classification  method + class count (or manual breaks) + ramp family.
 * @param tokens  the active theme's map tokens (ramp length + stop resolution).
 *
 * Returns `breaks` (ascending class edges, `stops.length + 1` of them) and
 * `stops` (one per class, each with its lower-edge value + ramp token id). The
 * class count is clamped to the ramp family's length so every class gets a
 * distinct step.
 */
export function classify(
  values: number[],
  classification: Classification,
  tokens: MapTokens,
): Choropleth {
  const method: ClassificationMethod = classification.method;
  const family = classification.rampTokenId ?? DEFAULT_RAMP_FAMILY;
  const maxClasses = rampLength(family, tokens);

  let breaks: number[];
  if (method === "manual") {
    breaks = normalizeManualBreaks(classification.breaks);
  } else {
    const requested = classification.classes ?? DEFAULT_CLASS_COUNT;
    const classCount = Math.max(2, Math.min(requested, maxClasses));
    const finite = values.filter((n) => Number.isFinite(n));
    // numericBreaks returns `steps` edges spanning min→max; k classes need k+1.
    breaks = numericBreaks(finite, classCount + 1, method === "equal" ? "equal" : "quantile");
  }

  const classCount = Math.max(1, breaks.length - 1);
  const stopTokens = sampleRampStops(family, classCount, tokens);
  const stops = stopTokens.map((tokenId, i) => ({ value: breaks[i]!, tokenId }));

  return { method, breaks, stops };
}
